import * as React from "react";
import {
  PageBase,
  RequestStickyMenu,
  ProtectedTabs,
  BackButton,
  LinkedSubFilesTab,
  RequestHistoryTab,
  ConditionTab,
} from "../../../../components";
import { ETLCodes } from "../../../../locales";
import { ERoutes } from "../../../../ERoutes";
import {Icon, Spinner, SpinnerSize, Tab} from "@blueprintjs/core";
import { useParams, useHistory } from "react-router";
import { useApiService, useTl } from "../../../../hooks";
import { useGlobalData } from "../../../../contexts";
import { useApiEffect } from "nsitools-react";
import { MembershipRequestDetailPage } from "./MembershipRequestDetailPage";
import { RequestApi, MembershipRequestApi, ERequestType, EMembershipRequestType, ERequestState } from "../../../../api";
import { BillTab } from "../../BillTab";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";

export interface IMembershipRequestItemPageProps {}

export const MembershipRequestItemPage: React.FunctionComponent<IMembershipRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = "0",
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();
  const history = useHistory();
  const api = useApiService(MembershipRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () => requestApi.getRequestInfo(ERequestType.MembershipRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const [typeCode, tcloading] = useApiEffect(async () => {
    const res = await api.getMembershipRequestTypeCodeFromRequestId(+id);
    return res;
  }, [id]);

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(
    async () => await api.close(requestInfo?.parentRequestId),
    [api, requestInfo?.parentRequestId],
  );

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.MembershipRequests),
          route: ERoutes.membershipRequest,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryRsh,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0) {
          console.log("id ", requestInfo.parentRequestId);
          return (
            <RequestStickyMenu
              requestId={requestInfo.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsMembershipRequest"
        onChange={(newTabId) => history.push(`${ERoutes.membershipRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.membershipRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<MembershipRequestDetailPage MembershipRequestId={+id} editMode={state === "edit"} />}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          disabled={+id <= 0 || (!tcloading && typeCode === EMembershipRequestType.Renewal)}
          panel={
            requestInfo ? (
              <ConditionTab
                requestId={requestInfo.parentRequestId}
                requestState={requestInfo.requestStateCode}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.Conditions}
              />
            ) : null
          }
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
